import { useContext } from 'react';

import { useState } from '@hookstate/core';

import EquipmentStatus from '@models/EquipmentStatus.model';

import AddIcon from '@assets/svg/add-menu.svg';
import UpIcon from '@assets/svg/table-up.svg';
import DownIcon from '@assets/svg/table-down.svg';
import SerachIcon from '@assets/svg/table-search.svg';
import SerachClearIcon from '@assets/svg/search-clear.svg';

import { StatusBadge } from '@components';

import { NavigationContext } from '@layout/navigation/navigation';

import {
  CustomStatusContext,
  SortCustomStatusTypes,
} from './CustomStatus.query';

import { TableContentLoading } from './CustomStatus.loading';

import {
  Container,
  Content,
  AddButton,
  AddButtonText,
  Header,
  Title,
  Line,
  Heading,
  Spacing,
  Divider,
  LineText,
  ColumnName,
  ColumnContainer,
  Buttons,
  Direction,
  InputContainer,
  InputSearch,
  SearchIconContainer,
  NoText,
  SearchButton,
  SearchClear,
  Wrapper,
  MenuDiv,
  ArrowWrapper,
} from './CustomStatus.styles';
import { CustomStatusMenu } from './components/CustomStatusMenu/CustomStatusMenu';

interface IColumnTitle {
  label: string;
  direction?: 'up' | 'down';
  onClick?: () => void;
}

const ColumnTitle = ({ label, direction, ...rest }: IColumnTitle) => {
  return (
    <ColumnContainer {...rest}>
      <ColumnName>{label}</ColumnName>
      {!!direction && (
        <Direction>
          <img src={direction === 'up' ? UpIcon : DownIcon} alt="direction" />
        </Direction>
      )}
    </ColumnContainer>
  );
};

interface ICustomStatusRaw {
  equipmentStatuses?: EquipmentStatus[];
}

const CustomStatusRaw = ({ equipmentStatuses }: ICustomStatusRaw) => {
  const { openCreateDialog, search, sort, direction } =
    useContext(CustomStatusContext);
  const { role } = useContext(NavigationContext);

  const searchLocal = useState<string>('');
  const searchEdited = useState<boolean>(false);

  const clickColumn = (column: SortCustomStatusTypes) => {
    if (sort.get() === column) {
      if (direction.get() === 'ASC') {
        direction.set('DESC');
        return;
      }
      if (direction.get() === 'DESC') {
        direction.set(undefined);
        return;
      }
      direction.set('ASC');
    } else {
      direction.set('ASC');
    }
    sort.set(column);
  };

  const directionResult = (
    column: SortCustomStatusTypes,
  ): 'up' | 'down' | undefined => {
    if (sort.get() === column) {
      if (direction.get() === 'ASC') {
        return 'up';
      }
      if (direction.get() === 'DESC') {
        return 'down';
      }
    }
    return undefined;
  };

  const searchState = search.get() !== searchLocal.get() || searchEdited.get();
  const clearState =
    search.get() === searchLocal.get() &&
    searchLocal.get() !== '' &&
    !searchEdited.get();

  const callSearch = () => {
    search.set(searchLocal.get());
    searchEdited.set(false);
  };

  const clearSearch = () => {
    searchLocal.set('');
    search.set('');
    searchEdited.set(false);
  };

  return (
    <Container>
      <Header>
        <Wrapper>
          <Title>Here you can manage and configure asset status</Title>
        </Wrapper>
        <Buttons>
          <InputContainer>
            <SearchIconContainer>
              <img src={SerachIcon} alt="search" />
            </SearchIconContainer>
            <InputSearch
              placeholder="Search"
              value={searchLocal.get()}
              onChange={e => {
                searchLocal.set(e.target.value);
                searchEdited.set(true);
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  callSearch();
                }
              }}
              search={searchState}
              clear={clearState}
              autoFocus
            />
            {searchState && (
              <SearchButton onClick={callSearch}>Search</SearchButton>
            )}
            {clearState && (
              <SearchClear onClick={clearSearch}>
                <img src={SerachClearIcon} alt="clear" />
              </SearchClear>
            )}
          </InputContainer>
          {role === 'admin' ? (
            <AddButton type="button" onClick={openCreateDialog}>
              <img src={AddIcon} alt="add status" />
              <AddButtonText>Add Status</AddButtonText>
            </AddButton>
          ) : (
            <div />
          )}
        </Buttons>
      </Header>

      <Spacing>
        <Heading>
          <ArrowWrapper>
            <ColumnTitle
              label="Name"
              onClick={() => clickColumn('equipment_status.name')}
              direction={directionResult('equipment_status.name')}
            />
            {direction.get() === undefined && <img src={UpIcon} alt="" />}
          </ArrowWrapper>
          <ColumnTitle
            label="Description"
            onClick={() => clickColumn('equipment_status.description')}
            direction={directionResult('equipment_status.description')}
          />
          <ColumnTitle
            label="Category"
            onClick={() => clickColumn('equipment_status.category')}
            direction={directionResult('equipment_status.category')}
          />
          <div />
        </Heading>
      </Spacing>

      {equipmentStatuses ? (
        <>
          <Divider />
          <Content>
            {equipmentStatuses.map(equipmentStatus => (
              <Spacing key={equipmentStatus.id}>
                <Line>
                  <LineText limitOfLines={1}>{equipmentStatus.name}</LineText>
                  {equipmentStatus.description ? (
                    <LineText limitOfLines={1}>
                      {equipmentStatus.description}
                    </LineText>
                  ) : (
                    <NoText>Empty</NoText>
                  )}

                  <StatusBadge label={equipmentStatus.category} />
                  <MenuDiv>
                    <CustomStatusMenu equipmentStatus={equipmentStatus} />
                  </MenuDiv>
                </Line>
              </Spacing>
            ))}
          </Content>
        </>
      ) : (
        <TableContentLoading />
      )}
    </Container>
  );
};

export { CustomStatusRaw };
